/**
 * trustCopy — Spec D3 trust-tier derivation + copy table (AC-D3-7).
 *
 * NODE-SAFE: no React/MUI imports — unit-tested from builder jest and shared
 * by TrustChip (this module) and any server-side rendering of trust labels.
 *
 * Tiers (glyph is never the only signal — labels always render):
 *   ⚪ simulated   — no evidence at all, or model-generated only
 *   ⚫ curated     — an operator touched it (edited/added sources), still no real signal
 *   🟢 confirmed   — real-signal entries confirm the thesis (n aggregated)
 *   🔴 contradicted — real-signal entries contradict it; CONTRADICTS OUTRANKS
 *                    CONFIRMS (mixed evidence must alarm, not reassure)
 */

export type TrustTier = 'simulated' | 'curated' | 'confirmed' | 'contradicted';

/**
 * Repo-local per-thesis evidence (design §4.4). D2 shipped the schema field at
 * ANALYSIS level as an open placeholder; the per-thesis yaml fix rides D5's
 * tarball cycle — until then this runtime type is the contract.
 */
export interface ThesisEvidence {
  /** Where the thesis came from: model run ('simulated') or operator work ('curated'). */
  origin?: string;
  /** Real-traffic evidence entries (filled by D5 release reports / experiments). */
  realSignal?: Array<{ kind?: string; n?: number; source?: string; at?: string }>;
  /** Linked experiment ids (informational). */
  experiments?: string[];
}

export interface TrustAssessment {
  tier: TrustTier;
  glyph: string;
  /** Short chip label, e.g. "confirmed (n=214)". */
  label: string;
  /** Aggregated sample size across the matching real-signal entries. */
  n?: number;
  /** One-line tooltip copy explaining what the tier means and what upgrades it. */
  meaning: string;
}

export const TRUST_COPY: Record<TrustTier, { glyph: string; meaning: string }> = {
  simulated: {
    glyph: '⚪',
    meaning: 'Model-generated only — no operator curation and no real-traffic signal yet. Treat as a hypothesis.',
  },
  curated: {
    glyph: '⚫',
    meaning: 'An operator reviewed or edited this thesis. Still unverified by real traffic.',
  },
  confirmed: {
    glyph: '🟢',
    meaning: 'Real-traffic signal confirms this thesis. Sample size shown as n.',
  },
  contradicted: {
    glyph: '🔴',
    meaning: 'Real-traffic signal CONTRADICTS this thesis — contradiction outranks any confirmations. Review before relying on it.',
  },
};

const sumN = (entries: Array<{ n?: number }>): number | undefined => {
  const total = entries.reduce((sum, e) => sum + (typeof e.n === 'number' && Number.isFinite(e.n) ? e.n : 0), 0);
  return total > 0 ? total : undefined;
};

/** AC-D3-7: ⚪ → ⚫ → 🟢 → 🔴 with contradicts-wins. Tolerant of any partial shape. */
export function deriveTrustTier(evidence?: ThesisEvidence | null): TrustAssessment {
  const realSignal = Array.isArray(evidence?.realSignal) ? evidence!.realSignal! : [];
  const contradicts = realSignal.filter(e => e?.kind === 'contradicts');
  const confirms = realSignal.filter(e => e?.kind === 'confirms');

  if (contradicts.length > 0) {
    const n = sumN(contradicts);
    return {
      tier: 'contradicted',
      glyph: TRUST_COPY.contradicted.glyph,
      label: n !== undefined ? `contradicted (n=${n})` : 'contradicted',
      ...(n !== undefined ? { n } : {}),
      meaning: TRUST_COPY.contradicted.meaning,
    };
  }
  if (confirms.length > 0) {
    const n = sumN(confirms);
    return {
      tier: 'confirmed',
      glyph: TRUST_COPY.confirmed.glyph,
      label: n !== undefined ? `confirmed (n=${n})` : 'confirmed',
      ...(n !== undefined ? { n } : {}),
      meaning: TRUST_COPY.confirmed.meaning,
    };
  }
  if (evidence?.origin === 'curated') {
    return { tier: 'curated', glyph: TRUST_COPY.curated.glyph, label: 'curated', meaning: TRUST_COPY.curated.meaning };
  }
  return { tier: 'simulated', glyph: TRUST_COPY.simulated.glyph, label: 'simulated only', meaning: TRUST_COPY.simulated.meaning };
}
